import type { z } from 'zod';

// Domain events (design §6.4). A module declares the events it publishes in its
// manifest; other modules subscribe by type. Event data is stored as JSON, so its
// schema should describe plain JSON values: no dates, maps or transforms.

/** An event type: `<module>.<name>`, such as `donations.accepted` or `fleet.party.ended`. */
export type EventType = `${string}.${string}`;

export interface EventDefinition<D extends z.ZodType> {
  type: EventType;
  data: D;
  /**
   * Days to keep this event once every subscriber has it, instead of core's default. Only ever
   * shorter: a longer value is ignored. For events that are many and only matter at the moment.
   */
  retentionDays?: number;
}

const EVENT: unique symbol = Symbol.for('itc.event');

export interface EventDef<D extends z.ZodType = z.ZodType> {
  readonly [EVENT]: true;
  readonly type: EventType;
  readonly data: D;
  readonly retentionDays: number | undefined;
}

const EVENT_TYPE = /^[a-z][a-z0-9]*(\.[a-z][a-z0-9_]*)+$/;

export function defineEvent<D extends z.ZodType>(definition: EventDefinition<D>): EventDef<D> {
  if (!EVENT_TYPE.test(definition.type)) {
    throw new Error(`Event type "${definition.type}" must be <module>.<name> in lower case`);
  }
  const { retentionDays } = definition;
  if (retentionDays !== undefined && (!Number.isInteger(retentionDays) || retentionDays < 1)) {
    throw new Error(`Event "${definition.type}" retentionDays must be a whole number of days, at least 1`);
  }
  return Object.freeze({ [EVENT]: true as const, type: definition.type, data: definition.data, retentionDays });
}

export function isEventDef(value: unknown): value is EventDef {
  return typeof value === 'object' && value !== null && EVENT in value;
}

/** What a publisher passes. */
export type EventInput<E extends EventDef> = z.input<E['data']>;

/** What a subscriber receives. */
export type EventData<E extends EventDef> = z.output<E['data']>;
